import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ProfaseModal from '../../commons/ProfaseModal';
import PageForm, { FormField } from '../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../store';
import { updateEntity } from './grupo-empresarial.reducer';

export interface GrupoEditarFormValues {
  alias: string;
  grpAliasEquilibrio: string;
  grpAliasDecomsa: string;
}

const fields: FormField<GrupoEditarFormValues>[] = [
  { name: 'alias', label: 'Alias', required: true, placeholder: 'Alias del grupo' },
  { name: 'grpAliasEquilibrio', label: 'Alias equilibrio', placeholder: 'Alias equilibrio' },
  { name: 'grpAliasDecomsa', label: 'Alias decomsa', placeholder: 'Alias decomsa' },
];

const ACCENT = '#6366f1';

export interface GrupoEditarModalProps {
  open: boolean;
  /** Mientras el modal global de 403 está visible: no mostrar el portal pero mantener estado (misma ruta). */
  forceHidden?: boolean;
  onClose: () => void;
  /** Tras guardar correctamente (p. ej. refrescar, navegar y toast). Si se define, no se llama onClose. */
  onSaved?: () => void;
  /** id del grupo en la URL / Redux */
  grupoId: string | undefined;
}

export default function GrupoEditarModal({ open, forceHidden = false, onClose, onSaved, grupoId }: GrupoEditarModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.grupoEmpresarial.updating);
  const updateSuccess = useAppSelector(s => s.grupoEmpresarial.updateSuccess);
  const entity = useAppSelector(s => s.grupoEmpresarial.entity) as Record<string, any> | null;

  const [form, setForm] = useState<GrupoEditarFormValues>({
    alias: '',
    grpAliasEquilibrio: '',
    grpAliasDecomsa: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof GrupoEditarFormValues, string>>>({});
  const submittedRef = useRef(false);

  useLayoutEffect(() => {
    if (!open) {
      submittedRef.current = false;
      return;
    }
    submittedRef.current = false;
    setErrors({});
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !grupoId) return;
    const e = entity;
    if (e?.id != null && String(e.id) === String(grupoId)) {
      setForm({
        alias: String(e.alias ?? ''),
        grpAliasEquilibrio: String(e.grpAliasEquilibrio ?? ''),
        grpAliasDecomsa: String(e.grpAliasDecomsa ?? ''),
      });
    }
  }, [open, grupoId, entity]);

  useEffect(() => {
    if (!open) {
      submittedRef.current = false;
      return;
    }
    if (!submittedRef.current || updating) return;
    if (updateSuccess) {
      submittedRef.current = false;
      if (onSaved) {
        onSaved();
      } else {
        onClose();
      }
    } else {
      submittedRef.current = false;
    }
  }, [open, updating, updateSuccess, onClose, onSaved]);

  const handleFieldChange = (name: keyof GrupoEditarFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!grupoId || entity?.id == null) return;
    const errs: Partial<Record<keyof GrupoEditarFormValues, string>> = {};
    if (!form.alias.trim()) errs.alias = 'El alias es requerido';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    submittedRef.current = true;
    dispatch(
      updateEntity({
        ...entity,
        id: entity.id,
        alias: form.alias.trim(),
        grpAliasEquilibrio: form.grpAliasEquilibrio.trim(),
        grpAliasDecomsa: form.grpAliasDecomsa.trim(),
      }),
    );
  };

  if (!open) return null;

  if (forceHidden) return null;

  return (
    <ProfaseModal
      open
      titleId="grupo-editar-modal-title"
      onBackdropMouseDown={ev => {
        if (ev.target === ev.currentTarget && !updating) onClose();
      }}
    >
      <h2 id="grupo-editar-modal-title" className="profase-modal-title">
        Editar grupo empresarial
      </h2>
      <p className="profase-modal-description">
        Modifique los datos del grupo y guarde los cambios.
      </p>
      <PageForm
        embedded
        fields={fields}
        values={form}
        errors={errors}
        isNew={false}
        saving={updating}
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={onClose}
        accentColor={ACCENT}
        submitLabel="Guardar cambios"
        cancelLabel="Cancelar"
      />
    </ProfaseModal>
  );
}
